import * as React from "react";
import {cx}       from "emotion";

import "./tab-listings.scss";

import {consumeContext} from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {UseRef}         from "@Common/react/use-ref";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {isDevXd}        from "@Common/ui-components/debug/is-dev";

import {ErrorMessage}      from "../components/error-message";
import {GalleryImage}      from "../components/gallery-image";
import {LoadingSpinner}    from "../components/loading-spinner";
import {MultiSelectFooter} from "../components/multi-select-footer";
import {NoticeMessage}     from "../components/notice-message";
import {Paginator}         from "../components/paginator";


export const TabListings = ({
    modal,
    viewType,
    numResultsPerPage,
    imageArea             = null,
    singleSelectOptions   = null,
    multiSelectOptions    = null,
    listing,
    orderId,
    isMobile,
    disallowSvgImages     = false,
    onPaginateNumResults,
}:{
    modal                 : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage     : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea            ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions  ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions   ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    listing               : Types.Apis.Models.Listing,
    orderId               : number,
    isMobile              : boolean,
    disallowSvgImages    ?: boolean,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("systemConstants"))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        isImageLoadingToEditor  : boolean,
        errorMessage            : string,
        pageNumber              : number,
    }>>) => UseState({
        defaultValue: {
            isImageLoadingToEditor: false,
            errorMessage: "",
            pageNumber: 1,
        },
        next,
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {

        if (state.value.isImageLoadingToEditor) {
            return (
                <div className="tab-content listings-results">
                    <div className="results">
                        <LoadingSpinner text="Applying Image"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    // prepare loader for order images
    .then("loaderPhotos", ({systemConstants}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryImage[]>>) => Load2({
        fetch: async () => {

            let photos = [];

            // convert listing photos to GalleryImage types
            let captionOrdinal = 1;
            for(let listingPhoto of listing.pictures) {
                if (!listingPhoto.pictureUrl) continue;

                photos.push({
                    id: captionOrdinal,
                    thumbnailUrl: listingPhoto.thumbUrl || listingPhoto.pictureUrl,
                    printUrl: listingPhoto.pictureUrl,
                    name: listingPhoto.caption || `${listing.location.streetAddress} Photo ${captionOrdinal}`,
                    fileSize: 0,
                    width: 0,
                    height: 0,
                    fileType: "",
                    dateCreated: "",
                    dateModified: "",
                });
                captionOrdinal++;
            }

            // find dimensions for each image and exclude any that cannot be found
            await Promise.all(photos.map(photo => new Promise(resolve => {

                const remoteImage = new Image();
                remoteImage.onload = () => {

                    if (!remoteImage.width || !remoteImage.height) {
                        if (isDevXd()) console.log("PhotoDashboard TabListings.loaderPhotos found missing dimensions from", photo.printUrl, remoteImage);
                        photos.splice(photos.indexOf(photo), 1);
                        resolve(false);
                        return;
                    }

                    // remove webp format directive from the print URL
                    const [remoteFile, remoteQuery] = photo.printUrl.split("?");
                    if (remoteQuery && remoteQuery.includes("format=webp")) photo.printUrl = photo.printUrl.replace("format=webp", "");

                    photo.width = remoteImage.width;
                    photo.height = remoteImage.height;
                    photo.fileType = remoteFile.split(".")?.slice(-1)[0]?.toLowerCase();

                    // adjust webp print images to use imgproxy for jpeg conversion
                    if (photo.fileType === "webp") {
                        photo.printUrl = `${systemConstants.value.IMAGE_PROXY_URL}/image/jpg/${encodeURIComponent(photo.printUrl)}`;
                    }

                    resolve(true);
                }
                remoteImage.onerror = (err) => {
                    if (isDevXd()) console.log("PhotoDashboard TabListings.loaderPhotos error loading listing photos from", photo.printUrl, err);
                    resolve(false);
                }
                remoteImage.src = photo.printUrl;

            })));

            return photos;

        },
        captureException: true,
        next,
    }))

    // check if pagination is required
    .then("includePagination", ({loaderPhotos}, next:Link<JSX.Element, boolean>) => next(isMobile && !!loaderPhotos.value?.length))

    // render the images list
    .then("imagesContents", ({state, apis, company, loaderPhotos, includePagination}, next:Link<JSX.Element>) => {

        // return until photo data exists
        if (!loaderPhotos.value || loaderPhotos.loading) {
            return next(<LoadingSpinner text="Loading Listing Images"/>);
        }

        let photos = loaderPhotos.value;
        if (includePagination) photos = photos.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);

        // add a message for empty listing gallery
        if (!photos.length) return next(<NoticeMessage text="There are currently no listing images available."/>);

        // render the image list
        return next(<>
            {photos.map((photo) => (
            <GalleryImage
                key={photo.id}
                id={photo.id}
                imageName={photo.name}
                imageAttributionData={photo.attribution}
                thumbnailUrl={photo.thumbnailUrl}
                viewType={viewType}
                fileType={photo.fileType}
                fileSize={photo.fileSize}
                width={photo.width}
                height={photo.height}
                areaWidth={imageArea?.widthInches}
                areaHeight={imageArea?.heightInches}
                photoQualitySettings={imageArea?.photoQualitySettings}
                allowLowQuality={company.prefs.allowLowQualityPhotosProperty}
                disabled={photo.fileType === "svg" && disallowSvgImages}
                selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "order" && x.originalImageId === photo.id)+1}
                onClick={async () => {

                    // apply the image for single select mode
                    if (singleSelectOptions) {

                        // set the image as applying
                        state.onChange({...state.value, isImageLoadingToEditor: true, errorMessage: ""});

                        // copy the image to the current order gallery
                        const savedImage = await apis.next.gallery.listings.saveImage({
                            listingId: listing._id,
                            remoteUrl: photo.printUrl,
                            imageName: photo.name,
                            orderId,
                        });
                        if (savedImage.errorMessage) {
                            state.onChange({...state.value, isImageLoadingToEditor: false, errorMessage: "There was an error loading your photo. Please try another."});
                            return;
                        }

                        // apply the image
                        singleSelectOptions.onApply(imageArea, "order", savedImage.id, savedImage.printUrl, modal.close);

                    }

                    // select the image for multi-select mode
                    else if (multiSelectOptions) {

                        // remove the current selection if already selected
                        const existingSelection = multiSelectOptions.selectedImages.find(x => x.assetType === "order" && x.originalImageId === listing._id);
                        if (existingSelection) return multiSelectOptions.onSelect(existingSelection);

                        // TODO: Don't copy image until finalizing the import - This creates a delay and will create a new order gallery image for selection if they select-unselect-select-etc
                        // copy the image to the order gallery
                        const savedImage = await apis.next.gallery.listings.saveImage({
                            listingId: listing._id,
                            remoteUrl: photo.printUrl,
                            imageName: photo.name,
                            orderId,
                        });
                        if (savedImage.errorMessage) return;

                        multiSelectOptions.onSelect({
                            assetType: "order",
                            imageId: savedImage.id,
                            imagePath: savedImage.printUrl,
                            thumbnailUrl: savedImage.thumbnailUrl,
                            originalImageId: listing._id,
                        });

                    }

                }}
            />))}
        </>);
    })

    // create the results area ref hook for controlling scroll position
    .then("resultsAreaRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // render the tab contents
    .then(({state, imagesContents, loaderPhotos, includePagination, resultsAreaRef}) => (<>
        <div className={cx("tab-listings-ptd tab-content listings-gallery", "viewtype-"+viewType)}>
            <div className="results" ref={ref => resultsAreaRef.set(ref)}>
                <ErrorMessage text={state.value.errorMessage}/>
                <div className="source-images">
                    {imagesContents}
                </div>
            </div>
            {includePagination && (
                <Paginator
                    numItems={loaderPhotos.value.length}
                    pageNumber={state.value.pageNumber}
                    itemsPerPage={numResultsPerPage}
                    onPaginateNumResults={numResults => {
                        resultsAreaRef.get().scrollTop = 0;
                        onPaginateNumResults(numResults);
                    }}
                    onPaginatePageNumber={pageNumber => {
                        resultsAreaRef.get().scrollTop = 0;
                        state.onChange({...state.value, pageNumber});
                    }}
                />
            )}
        </div>
        {multiSelectOptions && (
            <MultiSelectFooter
                maxSelections={multiSelectOptions.maxSelections}
                numSelected={multiSelectOptions.selectedImages.length}
                onApply={() => {
                    state.onChange({...state.value, isImageLoadingToEditor: true});
                    multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
                }}
                onClear={multiSelectOptions.onClearSelections}
            />
        )}
    </>))

    .run()

);
